import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import type * as Yup from 'yup';

import {
  type MutationRouteBarrelTypes,
  MutationRouteBarrelVals,
  type QueryRouteBarrelKeys,
} from '@core/api';

import type { QueryError } from '../utils/Query';
import { SlabConfig } from '../utils/SlabConfig';
import { useSlabAuth } from './useSlabAuth';
import { UrlFromParams } from './useSlabQuery';

type Params = Record<string, string | number | boolean>;

/**
 * Maximum number of times to retry a mutation that fails with a 409 Conflict
 * (PostgreSQL serialization failure). The server returns 409 when concurrent
 * transactions conflict under SERIALIZABLE isolation, per PostgreSQL docs:
 * https://www.postgresql.org/docs/current/transaction-iso.html#XACT-SERIALIZABLE
 */
const SERIALIZATION_RETRY_COUNT = 3;

export type UpdateType<T> = {
  args: T;
  schema?: Yup.BaseSchema<any> | { cast: (data: any) => any };
  signal?: AbortSignal;
};

export const useSlabMutation = <
  TKey extends keyof typeof MutationRouteBarrelVals,
  TArgs extends MutationRouteBarrelTypes[TKey]['args'] = MutationRouteBarrelTypes[TKey]['args'],
  TReturn extends MutationRouteBarrelTypes[TKey]['returns'] =
    MutationRouteBarrelTypes[TKey]['returns'],
  TError extends QueryError = QueryError,
>(
  key: TKey,
  options?: Omit<UseMutationOptions<TReturn, TError, UpdateType<TArgs>, unknown>, 'mutationFn'>,
): UseMutationResult<TReturn, TError, UpdateType<TArgs>, unknown> => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useSlabAuth();

  const barrel = MutationRouteBarrelVals[key];
  const updateObject = async ({
    args,
    schema = { cast: (data: any) => data },
    signal,
  }: UpdateType<TArgs>): Promise<TReturn> => {
    // Check the user has a valid access token
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: SlabConfig.auth0.audience,
      },
    });

    // Extract the raw ID_Token into the authorization bearer header
    const headers = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const fullUrl = UrlFromParams({
      queryParams: ((args as any)?.queryParams as Params) ?? {},
      pathParams: ((args as any)?.pathParams as Params) ?? {},
      urlSplits: barrel.urlSplits,
    });

    // Run any schema transforms on data before submission
    const castBody =
      (args as any)?.body !== undefined ? schema.cast((args as any).body) : undefined;
    switch (barrel.method) {
      case 'POST':
        return axios
          .post<TReturn>(fullUrl, castBody, { ...headers, signal })
          .then((d) => barrel.new(d.data));
      case 'PUT':
        return axios
          .put<TReturn>(fullUrl, castBody, { ...headers, signal })
          .then((d) => barrel.new(d.data));
      case 'DELETE':
        return axios
          .delete<TReturn>(fullUrl, { ...headers, data: castBody, signal })
          .then((d) => barrel.new(d.data));
      // default to 'POST' since this will give a compile error if method is not implemented
      default:
        return axios
          .post<TReturn>(fullUrl, castBody, { ...headers, signal })
          .then((d) => barrel.new(d.data));
    }
    // TODO: I think there should be a .catch on these here that custom wraps the error and returns
    //   it to the page that called this mutation...
  };

  return useMutation<TReturn, TError, UpdateType<TArgs>, unknown>({
    retry: (failureCount, error) => {
      if (!isAxiosError(error)) return false;
      if (error.response?.status !== 409) return false;
      if (failureCount >= SERIALIZATION_RETRY_COUNT) return false;
      // Only retry 409s explicitly marked as serialization failures. Other 409s
      // (e.g. resource conflicts, optimistic concurrency) should not be retried.
      const kind = (error.response.data as { kind?: string } | undefined)?.kind;
      return kind === 'serialization_failure';
    },
    // Exponential backoff with upper-half jitter: each delay is sampled from
    // [base, 1.5 * base) to spread concurrent retries that arrive at roughly
    // the same time and reduce thundering-herd collisions.
    retryDelay: (attemptIndex) => {
      const base = Math.min(200 * 2 ** attemptIndex, 2000);
      const jitterMs = Math.random() * base * 0.5;
      return base + jitterMs;
    },
    ...options,
    mutationFn: updateObject,
    onSuccess: (d, v, c, m) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          barrel.invalidations.includes(query.queryKey[1] as QueryRouteBarrelKeys),
      });
      if (options?.onSuccess !== undefined) {
        options.onSuccess(d, v, c, m);
      }
    },
  });
};
